/*
 * - Exposes class and methods to interact with the Dashboard API
 */

import request, { REQUEST_METHODS } from '@api/Request';
import { DashboardEndpoints } from './config';
import {
  CancelDashboardRequestResponse,
  DashboardUserRequest,
  FlattenedProjectRequests,
  GetRegulatorHelpRequestsPayload,
  GetRegulatorHelpRequestsResponse,
  GetUserRequests,
  GetUserRequestsResponse,
  UserProject,
  WithdrawDashboardRequestResponse,
} from './types';
import { RequestType, ResponseStatuses } from '@src/utils/enums';

export default class DashboardService {
  // Queries ---------------------------------------------------------------------------------------

  public static readonly getUserRequests = async (archived: boolean): Promise<GetUserRequestsResponse> => {
    const response: GetUserRequests = await request({
      url: DashboardEndpoints.getUserRequests(),
      method: REQUEST_METHODS.GET,
      params: { archived },
    });
    if (response.status !== ResponseStatuses.Success) {
      return { flattenedRequests: [], fullProjectData: [], requestsByProject: [] };
    }
    const fullProjectData: DashboardUserRequest[] = response.data;
    const flattenedRequests: FlattenedProjectRequests = [];
    const requestsByProject: UserProject[] = [];

    for (const project of fullProjectData) {
      const { projectID, projectName, location, requests = [] }: DashboardUserRequest = project;
      const projectData: UserProject = {
        projectId: projectID,
        projectName,
        location,
        projectRequests: [],
      };

      for (const request of requests) {
        const {
          createdDate,
          daNumber,
          hasOpenAdditionalInfoRequest,
          helpInquiryStatus,
          helpInquiryID,
          jdRequests = [],
          modifiedDate,
          nonPCNRequests: nonPcnRequests = [],
          noPermitRequireds = [],
          ormAdditionalInfoActionID,
          permission,
          permits = [],
          pmContactInfo,
          pmEmail,
          pmName,
          preApplications = [],
          requestDate,
          requestID,
          requestStatus,
          requestType,
          version,
        } = request;

        let itemIds: number[] = [];

        if (requestType === RequestType.ViolationComplaint) {
          itemIds = [requestID];
        } else {
          if (preApplications.length > 0) {
            itemIds = preApplications.map((preApp) => preApp.preAppID);
          }
          if (jdRequests.length > 0) {
            itemIds = jdRequests.map((jdReq) => jdReq.jdRequestID);
          }
          if (permits.length > 0) {
            itemIds = permits.map((permit) => permit.permitID);
          }
          if (nonPcnRequests.length > 0) {
            itemIds = nonPcnRequests.map((nonPcn) => nonPcn.nonPcnRequestID);
          }
          if (noPermitRequireds.length > 0) {
            itemIds = noPermitRequireds.map((noPermit) => noPermit.noPermitRequiredID);
          }
        }

        if (itemIds.length > 0) {
          for (const id of itemIds) {
            flattenedRequests.push({
              createdDate,
              daNumber,
              district: location?.district,
              hasOpenAdditionalInfoRequest,
              helpInquiryStatus,
              helpInquiryID,
              id,
              modifiedDate,
              ormAdditionalInfoActionID,
              permission,
              pmContactInfo,
              pmEmail,
              pmName,
              projectID,
              projectName,
              requestDate,
              requestID,
              requestedPermitType: permits[0]?.requestedPermitType ?? null,
              requestStatus,
              requestType,
              version,
            });
            projectData.projectRequests.push({
              id,
              permission,
              requestDate,
              requestId: id,
              requestStatus,
              requestType,
              version,
            });
          }
        }
      }

      requestsByProject.push(projectData);
    }
    return {
      flattenedRequests,
      fullProjectData,
      requestsByProject,
    };
  };

  public static readonly getRegulatorHelpRequests = async (
    params: GetRegulatorHelpRequestsPayload
  ): Promise<GetRegulatorHelpRequestsResponse> => {
    const response: GetRegulatorHelpRequestsResponse = await request({
      url: DashboardEndpoints.getRegulatorHelpRequests(),
      method: REQUEST_METHODS.GET,
      params,
    });

    return response;
  };

  // Mutations -------------------------------------------------------------------------------------

  public static readonly cancelRequest = (
    projectId: number,
    requestId: number,
    version: number
  ): Promise<CancelDashboardRequestResponse> =>
    request({
      url: DashboardEndpoints.cancelRequest(),
      method: REQUEST_METHODS.PUT,
      params: { projectId, requestId, version },
    });

  public static readonly withdrawRequest = (
    projectId: number,
    requestId: number,
    version: number
  ): Promise<WithdrawDashboardRequestResponse> =>
    request({
      url: DashboardEndpoints.withdrawRequest(),
      method: REQUEST_METHODS.PUT,
      params: { projectId, requestId, version },
    });
}
